import { kvGet, kvSet, type CalendarEventRow, type TimeoffRequestRow } from '@routeback/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { useApp } from '../../lib/app';
import { supabase } from '../../lib/supabase';
import { overlaps, type OffEntry } from './timeoff';

/**
 * Who's approved off between `from` and `to`, for the team calendar and the
 * conflict check. Drivers can't read each other's requests, so in the cloud
 * this asks `timeoff_calendar` (first names only) and keeps the answer on the
 * phone for when there's no signal. In local mode the only person is you.
 */
export function useTeamOff(from: string, to: string): { off: OffEntry[]; loaded: boolean } {
  const app = useApp();
  const key = `timeoff-cal:${app.company.id}:${from}:${to}`;
  const [state, setState] = useState<{ key: string; off: OffEntry[]; loaded: boolean }>({ key, off: [], loaded: false });
  const mine = useMyTimeoff();
  const lastSync = app.syncStatus.lastSyncAt;

  useEffect(() => {
    if (!supabase || !from || !to || to < from) return;
    let alive = true;
    void (async () => {
      const cached = await kvGet<OffEntry[]>(key);
      if (alive && cached) setState({ key, off: cached, loaded: true });
      const { data, error } = await supabase.rpc('timeoff_calendar', { c: app.company.id, from_date: from, to_date: to });
      if (!alive) return;
      if (error) {
        setState((s) => (s.key === key ? { ...s, loaded: true } : { key, off: cached ?? [], loaded: true }));
        return;
      }
      const rows = (data ?? []) as OffEntry[];
      setState({ key, off: rows, loaded: true });
      await kvSet(key, rows);
    })();
    return () => { alive = false; };
  }, [key, from, to, app.company.id, lastSync]);

  if (!supabase) {
    // Local mode: approved requests on this device are the whole company.
    const off = mine
      .filter((r) => r.status === 'approved' && r.start_date && r.end_date && overlaps(r.start_date, r.end_date, from, to))
      .map((r) => ({ request_id: r.id, user_id: r.user_id, first_name: app.userName.split(' ')[0] || 'You', start_date: r.start_date!, end_date: r.end_date! }));
    return { off, loaded: true };
  }
  return state.key === key ? { off: state.off, loaded: state.loaded } : { off: [], loaded: false };
}

/** My own requests (all statuses), newest first. Live from the phone. */
export function useMyTimeoff(): TimeoffRequestRow[] {
  const app = useApp();
  return useLiveQuery(
    async () => (await app.db.table<TimeoffRequestRow, string>('timeoff_requests').where('company_id').equals(app.company.id).toArray())
      .filter((r) => !r.deleted_at && r.user_id === app.userId)
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [app.db, app.company.id, app.userId],
    [] as TimeoffRequestRow[],
  );
}

/** Company calendar events (holidays, blackout days, events). */
export function useCalendarEvents(): CalendarEventRow[] {
  const app = useApp();
  return useLiveQuery(
    async () => (await app.db.table<CalendarEventRow, string>('calendar_events').where('company_id').equals(app.company.id).toArray()).filter((e) => !e.deleted_at),
    [app.db, app.company.id],
    [] as CalendarEventRow[],
  );
}
